import {
  feedbackcard,
  Field,
  FilteractionObject,
  FilterQuery
} from '@yes.technology/react-toolkit'
import { Form } from 'component'
import { resolveQuery } from 'filteraction/FilteractionResolver'
import {
  SetStateAction,
  useCallback,
  useEffect,
  useReducer,
  useState
} from 'react'
import { useTranslation } from 'react-i18n-lite'
import { FieldSettings } from 'shared/types'
import { useSiteState } from 'siteState/shared'
import useLocationState from '../../hooks/useLocationState'
import { toYesFilter } from '../../shared/toYesFilter/toYesFilter'
import { FieldValues, FilterAreaLayout, GroupedFieldValues } from '../../types'
import Section from '../Section/Section'

type Action = {
  type?: 'merge' | 'set'
  payload: GroupedFieldValues
}

type FilterFormProps = {
  elementUuid: string
  filterAreaLayout?: FilterAreaLayout
  initialGroupedFieldValues: GroupedFieldValues
  sections: Field[][]
  title?: string
  onSubmit?: (query: ReturnType<typeof toYesFilter>) => void
  uuidCurrentSitemodelObjectclass?: string
  defaultExecutedFilteraction?: string
  navigationSliderBackgroundColor?: string
  navigationSliderColor?: string
  style?: React.CSSProperties
  fieldSettings?: FieldSettings
  filteractionOptions?: FilteractionObject[]
  clearFilterQuery?: (
    value: SetStateAction<Partial<FilterQuery | undefined>>
  ) => void
  enableComposition?: boolean
}

const FilterForm = ({
  elementUuid,
  filterAreaLayout,
  initialGroupedFieldValues,
  sections,
  title,
  onSubmit,
  uuidCurrentSitemodelObjectclass,
  defaultExecutedFilteraction,
  navigationSliderBackgroundColor,
  navigationSliderColor,
  style,
  fieldSettings,
  filteractionOptions,
  clearFilterQuery,
  enableComposition
}: FilterFormProps) => {
  const {
    shouldResetFilters,
    setShouldResetFilters,
    persistentSelection,
    locationState
  } = useLocationState()

  const { t, language } = useTranslation()

  const fieldValuesReducer = (
    state: GroupedFieldValues,
    action: Action | null
  ) => {
    if (action === null) {
      return initialGroupedFieldValues
    }

    if (action.type === 'set') {
      return action.payload
    }

    return {
      ...state,
      ...Object.fromEntries(
        Object.entries(action.payload).map(([actionGroupKey, actionGroup]) => [
          actionGroupKey,
          { ...state[actionGroupKey], ...actionGroup }
        ])
      )
    }
  }

  const [groupedFieldValues, handleChange] = useReducer(
    fieldValuesReducer,
    initialGroupedFieldValues
  )

  useEffect(() => {
    const filtersChangedInPersistentSelection =
      !locationState?.sitemodelHistory?.length &&
      persistentSelection?.FilterFields &&
      JSON.stringify(groupedFieldValues) !==
        JSON.stringify(persistentSelection.FilterFields)

    if (filtersChangedInPersistentSelection) {
      handleChange({
        type: 'set',
        payload: persistentSelection.FilterFields as GroupedFieldValues
      })
    }
  }, [
    groupedFieldValues,
    locationState?.sitemodelHistory?.length,
    persistentSelection?.FilterFields
  ])

  useEffect(() => {
    if (shouldResetFilters) {
      handleChange(null)

      setShouldResetFilters(false)
    }
  }, [shouldResetFilters, setShouldResetFilters])

  const [composition, handleComposition] = useState('1')

  const [_, setSiteFieldValues] =
    useSiteState<GroupedFieldValues>('field-values')

  const [, setSiteFieldComposition] = useSiteState<string>('field-composition')

  const handleClear = useCallback(() => {
    handleComposition('1')
    handleChange(null)
    clearFilterQuery?.(undefined)
  }, [clearFilterQuery])

  const submitFieldValues = useCallback(
    async (fieldValues: GroupedFieldValues, composition: string) => {
      const groupedValuesNoEmpty = removeEmptyValuesGrouped(fieldValues)

      const query = toYesFilter({
        groups: groupedValuesNoEmpty,
        composition,
        language
      })

      if (!query) {
        return
      }

      try {
        const resolvedQuery = await resolveQuery(
          query,
          console.log,
          [],
          true,
          true
        )

        resolvedQuery && onSubmit?.(resolvedQuery)
        resolvedQuery && setSiteFieldValues(groupedValuesNoEmpty)
        setSiteFieldComposition(composition)
      } catch (error) {
        console.error('Error fetching filteraction', error)

        feedbackcard(t('generic-error'), {
          type: 'error',
          message: t('filter-fields.invalid-filter-action-format')
        })
      }
    },
    [language, onSubmit, setSiteFieldValues, setSiteFieldComposition, t]
  )

  const onSubmitForm = (event: React.FormEvent) => {
    event.preventDefault()
    submitFieldValues(groupedFieldValues, composition)
  }

  const onFieldChange = useCallback((state: GroupedFieldValues) => {
    handleChange({
      type: 'merge',
      payload: state
    })
  }, [])

  return (
    <Form onSubmit={onSubmitForm} style={style}>
      <Section
        initialFieldValues={initialGroupedFieldValues[1]}
        handleChange={onFieldChange}
        executeFilter={submitFieldValues}
        {...{
          composition,
          sections,
          groupedFieldValues,
          handleClear,
          handleComposition,
          title,
          enableComposition,
          uuidCurrentSitemodelObjectclass,
          navigationSliderBackgroundColor,
          navigationSliderColor,
          filterAreaLayout,
          defaultExecutedFilteraction,
          elementUuid,
          fieldSettings,
          filteractionOptions
        }}
      />
    </Form>
  )
}

export function removeEmptyValues<T extends FieldValues>(obj: T) {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, item]) =>
      Array.isArray(item.value) ? item.value.length > 0 : item.value
    )
  )
}

function removeEmptyValuesGrouped<T extends GroupedFieldValues>(
  groups: T
): GroupedFieldValues {
  return Object.fromEntries(
    Object.entries(groups).map(([group, values]) => [
      group,
      removeEmptyValues(values)
    ])
  )
}

export default FilterForm
